import { useRef, type ReactNode } from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import { cn } from '@/lib/utils';
import { RecordOpener, registerSheetOpener } from './RecordOpener';

export interface BottomSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Required for screen readers; hide it visually with `hideTitle`. */
	title: ReactNode;
	description?: ReactNode;
	hideTitle?: boolean;
	/** Header slots, e.g. a Cancel and a Done BottomSheetTextButton. */
	headerLeft?: ReactNode;
	headerRight?: ReactNode;
	/** Pinned under the scrolling body: Reset / Apply, Save. */
	footer?: ReactNode;
	children?: ReactNode;
	/**
	 * auto: as tall as the content (menus, short forms)
	 * large: always most of the screen (pickers with search, long forms)
	 */
	size?: 'auto' | 'large';
	/** Set false while a form has unsaved changes so a drag can't throw them away. */
	dismissible?: boolean;
	className?: string;
	bodyClassName?: string;
	/** Runs when the sheet hands focus back; preventDefault() keeps focus where an action put it. */
	onCloseAutoFocus?: (event: Event) => void;
}

/**
 * A native-style bottom sheet for phones: drag it down anywhere to close,
 * with a grabber, a header, a scrolling body and an optional pinned footer.
 * It clears the home indicator and the iOS keyboard.
 *
 * Use it for phone-only surfaces (filters, pickers, quick forms). For a
 * dialog that should stay a dialog on desktop, use `DialogContent`, which is
 * already a bottom sheet on phones.
 */
export function BottomSheet({
	open,
	onOpenChange,
	title,
	description,
	hideTitle = false,
	headerLeft,
	headerRight,
	footer,
	children,
	size = 'auto',
	dismissible = true,
	className,
	bodyClassName,
	onCloseAutoFocus,
}: BottomSheetProps) {
	const hasHeaderButtons = Boolean(headerLeft || headerRight);
	const returnFocus = useRef<HTMLElement | null>(null);
	return (
		<DrawerPrimitive.Root open={open} onOpenChange={onOpenChange} dismissible={dismissible} shouldScaleBackground={false}>
			<DrawerPrimitive.Portal>
				<DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
				<DrawerPrimitive.Content
					ref={(node) => registerSheetOpener(node, returnFocus)}
					data-opener-sheet=""
					// No description: leave aria-describedby off (VoiceOver would read the title twice).
					{...(description ? {} : { 'aria-describedby': undefined })}
					// Sheets are opened from plain buttons, not a Drawer.Trigger, so
					// Radix has nothing to hand focus back to; return it to the opener.
					// Leave focus alone if something already took it (a dialog the sheet
					// opened, a field).
					onCloseAutoFocus={(event) => {
						onCloseAutoFocus?.(event);
						const opener = returnFocus.current;
						returnFocus.current = null;
						if (event.defaultPrevented) return;
						event.preventDefault();
						const active = document.activeElement;
						if ((!active || active === document.body) && opener?.isConnected) opener.focus({ preventScroll: true });
					}}
					// Focus the sheet, not its first field, so iOS doesn't throw the
					// keyboard up over a sheet the person has only just opened.
					onOpenAutoFocus={(event) => {
						event.preventDefault();
						(event.currentTarget as HTMLElement | null)?.focus?.();
					}}
					className={cn(
						'fixed inset-x-0 bottom-0 z-50 flex max-h-[calc(var(--app-height)-var(--safe-top)-var(--call-bar-height,0px)-0.75rem)] flex-col rounded-t-[14px] bg-background outline-none',
						'dark:bg-zinc-900',
						size === 'large' && 'h-[calc(var(--app-height)-var(--safe-top)-var(--call-bar-height,0px)-0.75rem)]',
						className,
					)}
				>
					<RecordOpener into={returnFocus} />
					<div aria-hidden className="mx-auto mt-2 h-[5px] w-9 shrink-0 rounded-full bg-muted-foreground/30" />
					{hasHeaderButtons ? (
						<div className="grid h-12 shrink-0 grid-cols-[1fr_auto_1fr] items-center px-1.5">
							<div className="flex min-w-0 items-center justify-start">{headerLeft}</div>
							<DrawerPrimitive.Title
								className={cn('min-w-0 truncate px-1 text-center text-[17px] font-semibold', hideTitle && 'sr-only')}
							>
								{title}
							</DrawerPrimitive.Title>
							<div className="flex min-w-0 items-center justify-end">{headerRight}</div>
						</div>
					) : (
						<DrawerPrimitive.Title
							className={cn('shrink-0 px-4 pb-1 pt-3 text-center text-[17px] font-semibold', hideTitle && 'sr-only')}
						>
							{title}
						</DrawerPrimitive.Title>
					)}
					{description ? (
						<DrawerPrimitive.Description className="shrink-0 px-6 pb-2 text-center text-[13px] text-muted-foreground">
							{description}
						</DrawerPrimitive.Description>
					) : null}
					<div
						className={cn(
							'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-2',
							!footer && 'pb-[max(1rem,var(--safe-bottom))] kb-open:pb-4',
							bodyClassName,
						)}
					>
						{children}
					</div>
					{footer && (
						<div className="flex shrink-0 items-center gap-2 border-t border-border/60 px-4 pt-3 pb-[max(0.75rem,var(--safe-bottom))] kb-open:pb-3 [&>button]:h-11 [&>button]:flex-1 [&>button]:text-[15px]">
							{footer}
						</div>
					)}
				</DrawerPrimitive.Content>
			</DrawerPrimitive.Portal>
		</DrawerPrimitive.Root>
	);
}

/** iOS-style text button for BottomSheet header slots (Cancel, Done, Reset). */
export function BottomSheetTextButton({
	className,
	strong,
	type = 'button',
	...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { strong?: boolean }) {
	return (
		<button
			type={type}
			className={cn(
				'inline-flex h-11 min-w-11 items-center justify-center rounded-lg px-2.5 text-[17px] text-primary active:opacity-50 disabled:opacity-40',
				strong && 'font-semibold',
				className,
			)}
			{...props}
		/>
	);
}
